import { AsyncPipe, NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '@/app/+state/auth';
import { User } from '@/app/shared/interfaces';
import { SidebarService } from '@/app/core/services/sidebar.service';
import { TicketsWidgetService } from '@/app/core/services/tickets-widget.service';

@Component({
    selector: 'qc-user-menu',
    standalone: true,
    imports: [AsyncPipe, NgIf, RouterLink],
    template: `
        <button *ngIf="tickets.enabled()"
                type="button"
                class="report-problem"
                [class.expanded]="!sidebar.collapsed()"
                title="Report a problem"
                aria-label="Report a problem"
                (click)="tickets.open()">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" width="16" height="16" aria-hidden="true">
                <path d="M3.5 14.5V2M3.5 2.5h8.5l-1.75 3 1.75 3H3.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span *ngIf="!sidebar.collapsed()">Report a problem</span>
        </button>
        <a routerLink="/settings"
           class="user-menu"
           [class.expanded]="!sidebar.collapsed()"
           [title]="getDisplayName(user$ | async)">
            <div class="avatar">{{ getInitials(user$ | async) }}</div>
            <div class="user-text" *ngIf="!sidebar.collapsed()">
                <strong>{{ getDisplayName(user$ | async) }}</strong>
                <small>{{ (user$ | async)?.email }}</small>
            </div>
        </a>
    `,
    styles: [`
        .report-problem {
            display: flex;
            align-items: center;
            gap: 10px;
            height: 36px;
            padding: 0 12px;
            margin: 0 8px;
            background: transparent;
            border: 0;
            border-radius: 8px;
            color: rgba(255,255,255,0.7);
            font: inherit;
            font-size: 13px;
            cursor: pointer;
            text-align: left;
        }
        .report-problem.expanded { width: calc(100% - 16px); }
        .report-problem:hover { background: rgba(255,255,255,0.08); color: #ffffff; }
        .report-problem svg { flex-shrink: 0; }
        .report-problem:not(.expanded) {
            justify-content: center;
            width: 36px;
            padding: 0;
            margin: 0 auto;
        }
        .user-menu {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 12px;
            color: #ffffff;
            text-decoration: none;
            border-radius: 8px;
            margin: 4px 8px 6px;
        }
        .user-menu:hover { background: rgba(255,255,255,0.08); }
        .avatar {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            background: rgba(255,255,255,0.18);
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 12px;
            flex-shrink: 0;
        }
        .user-text {
            display: flex;
            flex-direction: column;
            min-width: 0;
            overflow: hidden;
        }
        .user-text strong {
            font-size: 13px;
            font-weight: 600;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .user-text small {
            font-size: 11px;
            opacity: 0.65;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .user-menu:not(.expanded) {
            justify-content: center;
            padding: 6px;
            margin: 4px 0 6px;
        }
    `],
})
export class UserMenuComponent {
    readonly #store = inject(Store);
    readonly sidebar = inject(SidebarService);
    readonly tickets = inject(TicketsWidgetService);
    public readonly user$ = this.#store.select(AuthSelectors.selectUser);

    getDisplayName(user: User | null): string {
        if (!user) return '';
        if (user.firstName || user.lastName) {
            return [user.firstName, user.lastName].filter(Boolean).join(' ');
        }
        return user.email;
    }

    getInitials(user: User | null): string {
        if (!user) return '';
        if (user.firstName && user.lastName) {
            return (user.firstName.charAt(0) + user.lastName.charAt(0)).toUpperCase();
        }
        if (user.firstName) {
            return user.firstName.charAt(0).toUpperCase();
        }
        return user.email.charAt(0).toUpperCase();
    }
}
